/** Characters that end a word in a venue name ("L'Osteria München - Pasing"). */
const SEPARATOR = /[\s\-–—·,/|:]/;
const LEADING_SEPARATORS = /^[\s\-–—·,/|:]+/;
const TRAILING_SEPARATORS = /[\s\-–—·,/|:]+$/;

/** Shortest stripped prefix worth removing: keeps "The Ivy" / "The Wolseley" intact. */
const MIN_PREFIX = 4;

/** Generous label width for drill charts: fits "Schwabing Leopoldstraße". */
export const OUTLET_LABEL_MAX = 22;

/**
 * The prefix every name shares, cut back to a word boundary, e.g.
 * "L'Osteria München " for six Munich outlets. Empty when fewer than two
 * names share it, when it is too short to matter, or when stripping would
 * leave any name empty.
 */
export function commonNamePrefix(names: readonly string[]): string {
  if (names.length < 2) return '';
  let lcp = names[0];
  for (const name of names.slice(1)) {
    let i = 0;
    while (i < lcp.length && i < name.length && lcp[i] === name[i]) i++;
    lcp = lcp.slice(0, i);
    if (!lcp) return '';
  }
  // Keep a shared first letter with its word ("Sendling" / "Schwabing").
  let end = lcp.length;
  const atBoundary = names.every(n => n.length === end || SEPARATOR.test(n[end]));
  if (!atBoundary) {
    while (end > 0 && !SEPARATOR.test(lcp[end - 1])) end--;
  }
  const prefix = lcp.slice(0, end);
  if (prefix.replace(TRAILING_SEPARATORS, '').length < MIN_PREFIX) return '';
  if (names.some(n => !remainder(n, prefix))) return '';
  return prefix;
}

function remainder(name: string, prefix: string): string {
  return name.slice(prefix.length).replace(LEADING_SEPARATORS, '').trim();
}

/** Truncate with a single ellipsis character. */
export function ellipsize(label: string, max = OUTLET_LABEL_MAX): string {
  return label.length > max ? `${label.slice(0, max - 1).trimEnd()}…` : label;
}

/**
 * Short, distinguishable chart labels for a set of venue names: drop the
 * prefix they all share, then ellipsise. Index-aligned with `names`; always
 * keep the full name for a title / tooltip.
 */
export function shortOutletLabels(
  names: readonly string[],
  max = OUTLET_LABEL_MAX,
): string[] {
  const prefix = commonNamePrefix(names);
  return names.map(n => ellipsize(prefix ? remainder(n, prefix) : n, max));
}
